import cx from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BpType } from '../../../../../../../enums';
import { IChartTooltipProps } from './types';

export const ChartTooltip = ({ payload, wrapperRef, coordinate }: IChartTooltipProps) => {
  const measurement = payload && payload.length ? payload[0].payload : null;
  const { t } = useTranslation();

  const marginLeft = useMemo(() => {
    if (!measurement || !wrapperRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const rightWrapperPosition = wrapperRect.x + wrapperRect.width;

    const tooltpWidth = measurement.bpType === BpType.PENDING ? 190 : 160;
    const tooltipWrapperWidth = document.querySelector('.recharts-tooltip-wrapper')?.clientWidth || 0;
    const rightTooltipPosition = wrapperRect.x + (coordinate?.x ?? 0) + tooltpWidth;
    const leftMargin = -(tooltpWidth - tooltipWrapperWidth);

    return rightTooltipPosition > rightWrapperPosition ? leftMargin : '';
  }, [measurement, coordinate, wrapperRef]);

  const minWidth = useMemo(() => {
    if (!measurement) return 'min-w-[160px] max-w-[160px]';
    return measurement.bpType === BpType.PENDING ? 'min-w-[190px] max-w-[190px]' : 'min-w-[160px] max-w-[160px]';
  }, [measurement]);

  if (!measurement) {
    return null;
  }

  return (
    <div
      className={cx('flex flex-col p-4 bg-background-base-primary border border-border-light rounded-xl shadow-high', [
        minWidth,
      ])}
      style={{
        marginLeft,
      }}
    >
      <span className="typography-title-2 text-text-primary">{measurement.tooltipText}</span>
      <span className="typography-paragraph-3 text-text-tertiary mt-0.5">
        {t('patient.chart.tooltip.bpAverage', { count: measurement.count })}
      </span>

      {measurement.bpType === BpType.PENDING || !measurement.bpType ? (
        <>
          <span className="typography-heading-6 text-text-secondary mt-4 mb-4">
            {t('patient.chart.tooltip.empty.title')}
          </span>
          <span className="typography-title-3 text-text-secondary">{t('patient.chart.tooltip.empty.subtitle')}</span>
          <ul className="list-disc pl-5 typography-paragraph-4 text-text-tertiary">
            <li>{t('patient.chart.tooltip.empty.reason.battery')}</li>
            <li>{t('patient.chart.tooltip.empty.reason.bracelet')}</li>
            <li>{t('patient.chart.tooltip.empty.reason.exercise')}</li>
            <li>{t('patient.chart.tooltip.empty.reason.hands')}</li>
            <li>{t('patient.chart.tooltip.empty.reason.strap')}</li>
          </ul>
        </>
      ) : (
        <ul className="mt-4">
          <li className="flex justify-between content-center mt-2 first:mt-0">
            <span className="typography-title-3 text-text-secondary">{t('patient.chart.tooltip.sys')}</span>
            <span className="typography-heading-6 text-text-primary">{measurement.sys}</span>
          </li>
          <li className="flex justify-between content-center mt-2 first:mt-0">
            <span className="typography-title-3 text-text-secondary">{t('patient.chart.tooltip.dia')}</span>
            <span className="typography-heading-6 text-text-primary">{measurement.dia}</span>
          </li>
          <li className="flex justify-between content-center mt-2 first:mt-0">
            <span className="typography-title-3 text-text-secondary">{t('patient.chart.tooltip.hr')}</span>
            <span className="typography-heading-6 text-text-primary">{measurement.hr}</span>
          </li>
        </ul>
      )}
    </div>
  );
};
